// VaultPage — /vault — R₿L Vault: revenue-sharing yield for depositors.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Landmark, Coins, Gift } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tVault } from "../i18n/vault";
import VaultStats from "../components/vault/VaultStats";
import DepositForm from "../components/vault/DepositForm";
import WithdrawForm from "../components/vault/WithdrawForm";
import YieldHistory from "../components/vault/YieldHistory";
import VoluntaryBurnPanel from "../components/vault/VoluntaryBurnPanel";

const fmt = (n) => Number(n).toLocaleString();

export default function VaultPage() {
  const { lang } = useLanguage();
  const t = (k) => tVault(lang, k);
  const { user, token } = useAuth();

  const [stats, setStats] = useState(null);
  const [dists, setDists] = useState(null);
  const [me, setMe] = useState(null);

  const loadPublic = useCallback(() => {
    fetch(`${API}/vault/stats`).then((r) => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/vault/distributions`).then((r) => r.json()).then(setDists).catch(() => {});
  }, []);

  const loadMe = useCallback(() => {
    if (!token) return;
    fetch(`${API}/vault/me`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setMe)
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    loadPublic();
    const id = setInterval(loadPublic, 60_000);
    return () => clearInterval(id);
  }, [loadPublic]);
  useEffect(() => { loadMe(); }, [loadMe]);

  const refresh = () => { loadPublic(); loadMe(); };

  return (
    <div className="min-h-screen bg-[#05060B] text-white" data-testid="vault-page">
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow/tokens" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-white/40 hover:text-amber-300" data-testid="vault-back">
          <ArrowLeft className="h-3.5 w-3.5" /> R₿L
        </Link>

        {/* Hero */}
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
            <Landmark className="mr-1.5 inline h-3.5 w-3.5" /> REVENUE SHARING · BASE L2
          </p>
          <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">
            {t("title")}
            {stats?.estimated_apy != null && (
              <span className="ml-3 align-middle rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 font-mono text-sm font-bold text-emerald-300">
                ~{stats.estimated_apy}% APY
              </span>
            )}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/55">{t("subtitle")}</p>
        </div>

        {/* How it works */}
        <div className="mt-8 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5" data-testid="vault-how-it-works">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80">{t("howItWorks")}</p>
          <p className="mt-2 text-sm leading-relaxed text-white/70">{t("howItWorksText")}</p>
        </div>

        {/* Global stats */}
        <div className="mt-8">
          <VaultStats stats={stats} />
        </div>

        {/* Personal panel */}
        {user && me && (me.total_deposited_rbl > 0 || me.total_yield_rbl > 0) && (
          <div className="mt-8 grid gap-3 md:grid-cols-2" data-testid="vault-personal">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                <Coins className="h-3.5 w-3.5 text-amber-300" /> {t("myDeposit")}
              </p>
              <p className="mt-2 font-mono text-2xl font-black" data-testid="vault-my-deposit">{fmt(me.total_deposited_rbl)} R₿L</p>
              {me.deposits?.some((d) => !d.is_eligible) && (
                <p className="mt-1 text-[10px] font-mono text-white/35">
                  {t("eligibleAt")}: {(me.deposits.find((d) => !d.is_eligible)?.eligible_at || "").slice(0, 10)}
                </p>
              )}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                <Gift className="h-3.5 w-3.5 text-emerald-300" /> {t("myYield")}
              </p>
              <p className="mt-2 font-mono text-2xl font-black text-emerald-300" data-testid="vault-my-yield">+{fmt(me.total_yield_rbl)} R₿L</p>
            </div>
          </div>
        )}

        {/* Deposit / Withdraw */}
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="mb-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">{t("deposit")}</h2>
            <DepositForm onDeposited={refresh} />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="mb-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">{t("withdraw")}</h2>
            {user ? <WithdrawForm me={me} onWithdrawn={refresh} /> : <p className="text-xs text-white/40">{t("loginFirst")}</p>}
          </div>
        </div>

        {/* Distribution history */}
        <div className="mt-10">
          <YieldHistory
            distributions={dists?.distributions}
            nextDistribution={dists?.next_distribution || stats?.next_distribution}
            myReceipts={me?.receipts}
          />
        </div>

        {/* Voluntary burn — divider + panel (additive section) */}
        <div className="mt-12 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="mt-10">
          <VoluntaryBurnPanel />
        </div>
      </section>
    </div>
  );
}
